import { useFormikContext } from 'formik';
import { isNil } from 'lodash';
import { ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AngleModificationMode,
  WorkPiecePosition,
} from '../../../../../../../util/part.util';
import { NumericInputWidget } from '../../../../../shared/component/widget/numeric-input';
import { PickerInput } from '../../../../../shared/component/widget/picker-input';
import { useFlowServices } from '../../../../../shared/hook/flow-service.hooks';
import {
  Flow,
  FlowItem,
  FlowItemArrayType,
  FlowItemText,
} from '../../../../../shared/model/flow.types';
import { FlowItemService } from '../../../../../shared/service/service.types';
import {
  instanceOfFlowItem,
  instanceOfFlowItemSection,
} from '../../../../../shared/util/flow-item.util';
import { BeaconWeldingData } from '../../../model/beacon-welding-data';
import { instanceOfFlowItemMultipassSectionParent } from '../../../util/beacon-welding.util';
import { MultipassSchedule } from './multipass-schedule';
import {
  MultipassComponentSettingsFormData,
  getWeldSequenceOptions,
  useManageMultipassScheduleValue,
} from './multipass.hooks';

interface MultipassFormProps {
  flow: Flow;
  flowItemService: FlowItemService;
  startItem: FlowItem;
  startWeldSequence: BeaconWeldingData.WeldSequenceMarkers;
  endWeldSequence: BeaconWeldingData.WeldSequenceMarkers;
}

export const useMultipassFormData = (props: MultipassFormProps) => {
  const {
    flow,
    flowItemService,
    startItem,
    startWeldSequence,
    endWeldSequence,
  } = props;
  const isEditing = instanceOfFlowItemMultipassSectionParent(startItem);

  const rootPass = useMemo(() => {
    if (isEditing) {
      return startItem.attributes.originalItems;
    }

    const startIndex = flow.items.findIndex(
      (item) => item.id === startWeldSequence.firstItem
    );
    const endIndex = flow.items.findIndex(
      (item) => item.id === endWeldSequence.lastItem
    );

    if (startIndex === -1 || endIndex === -1) {
      return [];
    }

    const originalItems = flow.items.slice(startIndex, endIndex + 1);
    const previousItem = flow.items[startIndex - 1];

    // If the previous item is a section, we need to add it to the original items
    if (previousItem && instanceOfFlowItemSection(previousItem)) {
      originalItems.unshift(previousItem);
    }

    return originalItems.filter(instanceOfFlowItem);
  }, [
    endWeldSequence.lastItem,
    flow.items,
    isEditing,
    startItem?.attributes?.originalItems,
    startWeldSequence.firstItem,
  ]);

  const allOriginalItems = useMemo(() => {
    return isEditing
      ? flowItemService.revertParentSection({
          parentItem: startItem,
          currentItems: flow.items,
        })
      : flow.items;
  }, [isEditing, flowItemService, startItem, flow.items]);

  const originalFlowItems = useMemo(() => {
    return allOriginalItems?.filter(instanceOfFlowItem);
  }, [allOriginalItems]);

  const originalTextArr = useMemo(() => {
    return flowItemService.buildFlowItemTextArr(originalFlowItems ?? []);
  }, [flowItemService, originalFlowItems]);

  const updatedTextMap = useMemo(() => {
    return flowItemService.buildFlowItemTextArr(flow.items).reduce(
      (acc, itemText) => {
        if (itemText) {
          acc[itemText.itemId] = itemText;
        }
        return acc;
      },
      {} as Record<string, FlowItemText>
    );
  }, [flow.items, flowItemService]);

  const sectionSequenceItemOptions = useMemo(() => {
    // Get the multipass section's original items if we're editing
    const multipassOriginalItems = isEditing
      ? (startItem.attributes?.originalItems as FlowItemArrayType[])?.filter(
          instanceOfFlowItem
        ) ?? []
      : undefined;

    return getWeldSequenceOptions({
      flowItemService,
      originalFlowItems,
      startItemId: startWeldSequence.firstItem,
      originalTextArr,
      updatedTextMap,
      multipassOriginalItems,
    });
  }, [
    flowItemService,
    originalFlowItems,
    startWeldSequence.firstItem,
    originalTextArr,
    updatedTextMap,
    isEditing,
    startItem.attributes?.originalItems,
  ]);

  return {
    rootPass,
    sectionSequenceItemOptions,
  };
};

interface MultipassFormContentProps {
  startItem: FlowItem;
  disabled: boolean;
  presentingEl?: HTMLElement;
  forceFullScreenModals?: boolean;
}

export const MultipassFormContent = (
  props: MultipassFormContentProps
): ReactElement => {
  const { startItem, disabled, presentingEl, forceFullScreenModals } = props;

  const { values } = useFormikContext<MultipassComponentSettingsFormData>();
  const { flow, flowItemService } = useFlowServices();
  const { numberOfPasses, angleMode, startWeldSequence, endWeldSequence } =
    values;

  const { rootPass, sectionSequenceItemOptions } = useMultipassFormData({
    flow,
    flowItemService,
    startItem,
    startWeldSequence,
    endWeldSequence,
  });

  const { i18n } = useTranslation();

  const showWorkPiecePosition = angleMode === AngleModificationMode.FIXED;

  const compareWeldSequences = (
    a: BeaconWeldingData.WeldSequenceMarkers,
    b: BeaconWeldingData.WeldSequenceMarkers
  ): boolean => {
    return a.firstItem === b.firstItem && a.lastItem === b.lastItem;
  };

  useManageMultipassScheduleValue();

  return (
    <div className="multipass-form-container">
      <NumericInputWidget
        name="numberOfPasses"
        label={i18n.t('common:number_of_passes')}
        disabled={disabled}
        transparentBackground={false}
        ionItemProps={{
          id: 'multipass-number-of-passes',
        }}
        triggerOn="ionChange"
      />
      <PickerInput
        name="startWeldSequence"
        label={i18n.t('welding:start_weld_sequence')}
        disabled={true}
        options={sectionSequenceItemOptions}
        compareWith={compareWeldSequences}
        transparentBackground={false}
      />
      <PickerInput
        name="endWeldSequence"
        label={i18n.t('welding:end_weld_sequence')}
        disabled={disabled}
        options={sectionSequenceItemOptions}
        compareWith={compareWeldSequences}
        transparentBackground={false}
      />
      <PickerInput<AngleModificationMode>
        name="angleMode"
        label={i18n.t('common:travel_work_angle')}
        options={[
          {
            label: i18n.t('common:relative'),
            value: AngleModificationMode.RELATIVE,
          },
          {
            label: i18n.t('common:fixed'),
            value: AngleModificationMode.FIXED,
          },
        ]}
        disabled={disabled}
        transparentBackground={false}
        ionItemProps={{
          id: 'multipass-angle-mode',
        }}
      />
      {showWorkPiecePosition && (
        <PickerInput<WorkPiecePosition>
          name="workpiecePosition"
          label={i18n.t('welding:workpiece_position')}
          options={[
            {
              label: i18n.t('welding:flat'),
              value: WorkPiecePosition.FLAT,
            },
            {
              label: i18n.t('welding:horizontal'),
              value: WorkPiecePosition.HORIZONTAL,
            },
            {
              label: i18n.t('welding:vertical'),
              value: WorkPiecePosition.VERTICAL,
            },
            {
              label: i18n.t('welding:overhead'),
              value: WorkPiecePosition.OVERHEAD,
            },
          ]}
          disabled={disabled}
          ionItemProps={{
            id: 'multipass-workpiece-position',
          }}
        />
      )}
      {!isNil(numberOfPasses) && (
        <MultipassSchedule
          rootPass={rootPass}
          numberOfPasses={Number(numberOfPasses)}
          disabled={disabled}
          presentingEl={presentingEl}
          forceFullScreenModals={forceFullScreenModals}
        />
      )}
    </div>
  );
};
